/**
 * File: owedUtils.ts
 * Path: apps/admin/src/shared/utils/owedUtils.ts
 * Description: The two money figures every page shows the same way (Liam,
 * admin UI audit C1, 4 Nov 2026). "Owed to you" is invoiced and not paid:
 * the books' Trade debtors for the business, the client's open invoice
 * balances for one client. "Still to bill" is signed and not invoiced yet:
 * the contract payments with no invoice. Without the books "Owed to you" is
 * "unavailable" and the page says so; it never falls back to the contracts.
 */

import {
  openInvoices,
  sameCustomer,
  type InvoiceLedger,
  type StillToBill,
} from "@idk/config";

export const OWED_TO_YOU = {
  label: "Owed to you",
  hint: "Money on invoices you sent that the client has not paid yet. In the books this is Trade debtors.",
  unavailable: "Only someone who can see the books sees what is owed on invoices.",
} as const;

export const STILL_TO_BILL = {
  label: "Still to bill",
  hint: "Money a client agreed to pay in a signed contract that you have not sent an invoice for yet.",
} as const;

/** One client, matched by id, or by name on an old invoice with no client id. */
export interface OwedScope {
  clientId: string;
  name: string;
}

export interface OwedToYou {
  cents: number;
  /** The part of it on invoices past their due date. */
  lateCents: number;
  lateCount: number;
}

export interface ToBill {
  cents: number;
  /** The part already due to be invoiced. */
  dueCents: number;
}

export interface OwedFigures {
  /** Null while loading; "unavailable" when the books can't be read (no finance permission, or not set up). */
  owed: OwedToYou | null | "unavailable";
  /** Null while loading. */
  toBill: ToBill | null;
}

const sum = (cents: readonly number[]): number => cents.reduce((s, c) => s + c, 0);

const owedToYou = (ledger: InvoiceLedger, scope?: OwedScope): OwedToYou => {
  const customer = scope && { clientId: scope.clientId, party: scope.name };
  const open = openInvoices(ledger).filter((r) => !customer || sameCustomer(r, customer));
  const late = open.filter((r) => r.status === "overdue");
  return {
    // The whole business is the Trade debtors account itself, so it agrees with the books to the cent.
    cents: customer ? sum(open.map((r) => r.balanceCents)) : ledger.controlCents,
    lateCents: sum(late.map((r) => r.balanceCents)),
    lateCount: late.length,
  };
};

const toBillFigures = (toBill: StillToBill, scope?: OwedScope): ToBill => {
  const rows = scope
    ? toBill.rows.filter((r) => sameCustomer({ clientId: r.clientId, party: r.clientName }, { clientId: scope.clientId, party: scope.name }))
    : toBill.rows;
  return {
    cents: sum(rows.map((r) => r.cents)),
    dueCents: sum(rows.filter((r) => r.dueToBill).map((r) => r.cents)),
  };
};

/** Owed to you and Still to bill, for the business or for one client. */
export const owedFigures = (
  ledger: InvoiceLedger | null | "unavailable",
  toBill: StillToBill | null,
  scope?: OwedScope,
): OwedFigures => ({
  owed: ledger === null || ledger === "unavailable" ? ledger : owedToYou(ledger, scope),
  toBill: toBill ? toBillFigures(toBill, scope) : null,
});

/**
 * Owed to you and Still to bill for a whole list of clients at once, from one
 * read of the books. Each client gets exactly what owedFigures gives with that
 * client as the scope (same sums), so a list row and the client's own page agree.
 */
export const owedFiguresByClient = (
  ledger: InvoiceLedger | null | "unavailable",
  toBill: StillToBill | null,
  clients: ReadonlyArray<{ id: string; name: string }>,
): Map<string, OwedFigures> =>
  new Map(clients.map((c) => [c.id, owedFigures(ledger, toBill, { clientId: c.id, name: c.name })]));
